import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import '../css/Forms.css'; // reuse your existing "po-*" wizard styles
import roles from '../data/role'; // <-- connect to role.js

export default function ProjectModal({ isOpen, onClose, onSubmit }) {
  const draftKey = 'PRF_FORM_WIZARD_V1';
  const formRef = useRef(null);
  const suppressPersist = useRef(false);

  // ---------- Helpers to read role.js ----------
  const uniq = (arr) => [...new Set(arr.filter(Boolean).map(s => String(s).trim()))];
  const inferGroup = (title) => {
    const v = String(title || '').toLowerCase();
    if (/(animator|artist|designer|illustrator|writer|composer|sound)/.test(v)) return 'Creative';
    if (/(programmer|developer|engineer|tech|sre|devops)/.test(v)) return 'Technical';
    if (/(qa|test)/.test(v)) return 'QA / Test';
    if (/(producer|pm|manager|lead|director)/.test(v)) return 'Management';
    return 'Other';
  };

  // Build grouped role options from role.js (dedupe across talent levels)
  const ROLE_GROUPS = useMemo(() => {
    const groups = {};

    const addToGroup = (group, roleTitle) => {
      if (!group) group = 'Other';
      if (!groups[group]) groups[group] = new Set();
      groups[group].add(roleTitle);
    };

    // Use category-like fields if available; otherwise infer from title
    (Array.isArray(roles) ? roles : []).forEach((r) => {
      const title = r?.role && String(r.role).trim();
      if (!title) return;
      const group =
        r.category ||
        r.department ||
        r.discipline ||
        r.roleFamily ||
        inferGroup(title);
      addToGroup(group, title);
    });

    // Fallback to your previous static list if role.js is empty/missing roles
    if (Object.keys(groups).length === 0) {
      const fallback = {
        Creative: [
          '3D Animator (Generalist)',
          'UI/UX Designer',
          'Sound Designer (FMOD/Wwise Ready)',
        ],
        Technical: ['Game Programmer'],
        Management: ['QA / Game Tester'],
      };
      Object.entries(fallback).forEach(([g, list]) => {
        groups[g] = new Set(list);
      });
    }

    // Convert Sets to sorted arrays
    const out = {};
    Object.entries(groups).forEach(([g, set]) => {
      out[g] = uniq(Array.from(set)).sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, []);

  const ALL_ROLE_TITLES = useMemo(
    () => Object.values(ROLE_GROUPS).flat(),
    [ROLE_GROUPS]
  );

  // ----- Default form -----
  const makeDefaultForm = () => ({
    // Section 1: Project Details
    projectName: '',
    projectDesc: '',
    startDate: '',
    duration: '',           // "<1m" | "1-3m" | "3-6m" | "6-12m" | "1y+"
    milestones: '',
    industryProjectType: 'Game Development',
    industryProjectTypeOther: '',
    projectLocationTimeZone: 'GMT+8',
    projectLocationTimeZoneOther: '',
    reportingLine: 'Client PM',
    reportingLineOther: '',

    // Section 2: Engagement Model
    engagementModel: '',    // 'EOR Standard' | 'EOR Premium' | 'Non-EOR' | 'White Label'
    engagementDuration: 'Short-Term',

    // Section 3: Team / Talent
    numberOfTalents: '',
    rolesNeeded: [],        // now stores role titles from role.js
    otherRole: '',
    availability: '',       // 'full' | 'part'
    seniorityLevel: 'Mid-Level',
    workArrangement: 'Remote',
    shiftRequirement: 'Day',
    languagesRequired: [],
    languagesOther: '',
    toolsSoftwareRequired: [],
    toolsSoftwareOther: '',
    securityComplianceNeeds: [],
    securityComplianceOther: '',

    // Section 4: Screening (defaults match your checkmarks)
    modules: {
      behavioral: false,    // ✅
      skills: false,        // ✅
      structured: false,    // ✅
      custom: false,        // ⬜
    },
    top3MustHaveSkills: '',
    niceToHaveSkills: '',
    portfolioRequirement: 'Yes',
    certificationRequirement: 'None',
    certificationOther: '',

    // Section 5: Deployment
    deploymentMode: '',     // 'standard-30' | 'fast-14'
    setupLevel: '',         // 'standard' | 'premium'
    nestingDuration: '2 weeks',
    supervisionLevel: 'Moderate',
    performanceMonitoringFrequency: 'Weekly',
    goLiveReadinessConfirmation: 'Client-led',
    knowledgeTransferMethod: 'Documentation',
    knowledgeTransferOther: '',

    // Section 6: Offboarding
    offboardingConfirm: false,
    exitInterviewRequired: 'No',
    finalPayrollBenefitsClearance: 'No',
    payrollBenefitsNotes: '',

    // Section 7: Notes & Special Instructions
    budgetRange: '',
    maxBudgetCap: '',
    paymentPreference: 'Monthly',
    specialInstructions: '',
    top3MustHaveProjectGoals: '',
    successMetricsKPIs: '',
    pointOfContact: {
      fullName: '',
      email: '',
      phoneWhatsapp: '',
    },

    // Section 8: Confirmation
    confirm: false,
  });

  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      const base = saved ? { ...makeDefaultForm(), ...JSON.parse(saved) } : makeDefaultForm();
      // Clean up any saved roles that no longer exist in role.js
      base.rolesNeeded = (base.rolesNeeded || []).filter(r => ALL_ROLE_TITLES.includes(r));
      return base;
    } catch {
      return makeDefaultForm();
    }
  });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0); // 0..7 (8 sections)

  const STEPS = useMemo(() => ([
    { key: 'details',  title: 'Project Details' },
    { key: 'engage',   title: 'Engagement Model' },
    { key: 'team',     title: 'Team / Talent Requirements' },
    { key: 'screen',   title: 'Screening & Assessments' },
    { key: 'deploy',   title: 'Deployment Preferences' },
    { key: 'offboard', title: 'Offboarding Preference' },
    { key: 'notes',    title: 'Notes & Special Instructions' },
    { key: 'confirm',  title: 'Confirmation' },
  ]), []);
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // ----- Persist draft -----
  useEffect(() => {
    if (suppressPersist.current) { suppressPersist.current = false; return; }
    try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch {}
  }, [form]);

  // Reset state when closed -> reopened
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(draftKey);
      const base = saved ? { ...makeDefaultForm(), ...JSON.parse(saved) } : makeDefaultForm();
      base.rolesNeeded = (base.rolesNeeded || []).filter(r => ALL_ROLE_TITLES.includes(r));
      setForm(base);
      setErrors({});
      setStep(0);
    } catch {
      setForm(makeDefaultForm());
      setErrors({});
      setStep(0);
    }
  }, [isOpen, ALL_ROLE_TITLES]);

  // ----- Utilities -----
  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateNested = (path, value) => {
    const [root, sub] = path.split('.');
    setForm(f => ({ ...f, [root]: { ...f[root], [sub]: value } }));
  };

  const toggleRole = (roleTitle) => {
    setForm(f => {
      const has = f.rolesNeeded.includes(roleTitle);
      const next = has ? f.rolesNeeded.filter(r => r !== roleTitle) : [...f.rolesNeeded, roleTitle];
      return { ...f, rolesNeeded: next };
    });
  };

  const toggleMultiSelect = (currentArray, setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const Check = ({ label, checked, onChange }) => (
    <label className="po-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="po-checkmark" /> <span>{label}</span>
    </label>
  );

  const saveDraft = () => {
    try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch {}
    alert('Draft saved.');
  };
  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    suppressPersist.current = true;
    setForm(makeDefaultForm());
    setErrors({});
    setStep(0);
  };

  // ----- Validation per step -----
  const validateStep = useCallback((s) => {
    const e = {};
    // step 0: Project Details
    if (s === 0) {
      if (!form.projectName.trim()) e.projectName = 'Please enter a project name.';
      if (!form.duration) e.duration = 'Select expected project duration.';
      if (!form.industryProjectType) e.industryProjectType = 'Please select Industry/Project Type.';
      if (!form.projectLocationTimeZone) e.projectLocationTimeZone = 'Please select Project Location/Time Zone.';
      if (!form.reportingLine) e.reportingLine = 'Please select Reporting Line.';
      if (form.industryProjectType === 'Other' && !form.industryProjectTypeOther.trim()) e.industryProjectTypeOther = 'Please specify industry/project type.';
      if (form.projectLocationTimeZone === 'Other' && !form.projectLocationTimeZoneOther.trim()) e.projectLocationTimeZoneOther = 'Please specify time zone.';
      if (form.reportingLine === 'Other' && !form.reportingLineOther.trim()) e.reportingLineOther = 'Please specify reporting line.';
    }
    // step 1: Engagement Model
    if (s === 1) {
      if (!form.engagementModel) e.engagementModel = 'Please select an engagement model.';
      if (!form.engagementDuration) e.engagementDuration = 'Please select Engagement Duration.';
    }
    // step 2: Team / Talent
    if (s === 2) {
      if (!form.numberOfTalents || Number(form.numberOfTalents) <= 0) e.numberOfTalents = 'Enter a valid number.';
      if ((form.rolesNeeded || []).length === 0 && !form.otherRole.trim()) {
        e.rolesNeeded = 'Select at least one role or specify Other.';
      }
      if (!form.availability) e.availability = 'Select an availability preference.';
      if (!form.seniorityLevel) e.seniorityLevel = 'Please select Seniority Level.';
      if (!form.workArrangement) e.workArrangement = 'Please select Work Arrangement.';
      if (!form.shiftRequirement) e.shiftRequirement = 'Please select Shift Requirement.';
      if (form.languagesRequired.includes('Other') && !form.languagesOther.trim()) e.languagesOther = 'Please specify other languages.';
      if (form.toolsSoftwareRequired.includes('Other') && !form.toolsSoftwareOther.trim()) e.toolsSoftwareOther = 'Please specify other tools/software.';
      if (form.securityComplianceNeeds.includes('Other') && !form.securityComplianceOther.trim()) e.securityComplianceOther = 'Please specify other security/compliance needs.';
    }
    // step 3: Screening & Assessments
    if (s === 3) {
      if (!form.top3MustHaveSkills.trim()) e.top3MustHaveSkills = 'Please enter Top 3 Must-Have Skills.';
      if (!form.portfolioRequirement) e.portfolioRequirement = 'Please select Portfolio Requirement.';
      if (!form.certificationRequirement) e.certificationRequirement = 'Please select Certification Requirement.';
      if (form.certificationRequirement === 'Other' && !form.certificationOther.trim()) e.certificationOther = 'Please specify certification requirement.';
    }
    // step 4: Deployment
    if (s === 4) {
      if (!form.deploymentMode) e.deploymentMode = 'Select a deployment mode.';
      if (!form.setupLevel) e.setupLevel = 'Choose a work enablement setup.';
      if (!form.nestingDuration) e.nestingDuration = 'Please select Nesting Duration.';
      if (!form.supervisionLevel) e.supervisionLevel = 'Please select Supervision Level.';
      if (!form.performanceMonitoringFrequency) e.performanceMonitoringFrequency = 'Please select Performance Monitoring Frequency.';
      if (!form.goLiveReadinessConfirmation) e.goLiveReadinessConfirmation = 'Please select Go-Live Readiness Confirmation.';
      if (!form.knowledgeTransferMethod) e.knowledgeTransferMethod = 'Please select Knowledge Transfer Method.';
      if (form.knowledgeTransferMethod === 'Other' && !form.knowledgeTransferOther.trim()) e.knowledgeTransferOther = 'Please specify knowledge transfer method.';
    }
    // step 5: Offboarding
    if (s === 5) {
      if (!form.offboardingConfirm) e.offboardingConfirm = 'You must confirm Exodia will manage offboarding.';
      if (!form.exitInterviewRequired) e.exitInterviewRequired = 'Please select Exit Interview requirement.';
      if (!form.finalPayrollBenefitsClearance) e.finalPayrollBenefitsClearance = 'Please select Final Payroll & Benefits Clearance.';
      if (form.finalPayrollBenefitsClearance === 'Yes' && !form.payrollBenefitsNotes.trim()) e.payrollBenefitsNotes = 'Please provide payroll & benefits notes.';
    }
    // step 6: Notes & Special Instructions
    if (s === 6) {
      if (!form.budgetRange.trim()) e.budgetRange = 'Please enter Budget Range.';
      if (!form.paymentPreference) e.paymentPreference = 'Please select Payment Preference.';
      if (!form.top3MustHaveProjectGoals.trim()) e.top3MustHaveProjectGoals = 'Please enter Top 3 Must-Have Project Goals.';
      if (!form.successMetricsKPIs.trim()) e.successMetricsKPIs = 'Please enter Success Metrics/KPIs.';
      if (!form.pointOfContact.fullName.trim()) e.pointOfContactFullName = 'Please enter Full Name.';
      if (!form.pointOfContact.email.trim()) e.pointOfContactEmail = 'Please enter Email Address.';
      if (!form.pointOfContact.phoneWhatsapp.trim()) e.pointOfContactPhone = 'Please enter Phone/WhatsApp.';
    }
    // step 7: Confirmation
    if (s === 7) {
      if (!form.confirm) e.confirm = 'You must confirm this project request.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form]);

  const goNext = () => { if (validateStep(step)) setStep(s => Math.min(totalSteps - 1, s + 1)); };
  const goBack = () => setStep(s => Math.max(0, s - 1));

  const onSubmitForm = (e) => {
    e.preventDefault();
    if (step !== totalSteps - 1) return;       // only submit on last step
    if (!validateStep(step)) return;
    onSubmit?.(form);
    try { localStorage.removeItem(draftKey); } catch {}
    alert('Project request submitted ✔️');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="po-overlay" onClick={onClose}>
      <div className="po-modal po-modal--wizard" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="po-header">
          <h2 className="po-title">Projects Form (PRF)</h2>
          <div className="po-header-actions">
            <button type="button" className="po-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {/* Progress */}
        <div className="po-progress">
          <div className="po-progress-top">
            <div className="po-progress-num">{progressPct}%</div>
            <div className="po-progress-label">Step {step + 1} of {totalSteps} — {STEPS[step].title}</div>
          </div>
          <div className="po-progress-bar">
            <div className="po-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <form ref={formRef} className="po-body" onSubmit={onSubmitForm}>
          {/* ====== STEP 1: DETAILS ====== */}
          {step === 0 && (
            <section className="po-card">
              <h3 className="po-h3">Section 1: Project Details</h3>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Project Name / Title</label>
                  <input
                    className="po-input"
                    placeholder="e.g., Project Aegis"
                    value={form.projectName}
                    onChange={(e) => updateField('projectName', e.target.value)}
                    required
                  />
                  {errors.projectName && <div className="po-error">{errors.projectName}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Target Start Date (optional)</label>
                  <input
                    type="date"
                    className="po-input"
                    value={form.startDate}
                    onChange={(e) => updateField('startDate', e.target.value)}
                  />
                </div>
              </div>

              <div className="po-field">
                <label className="po-label">Project Description / Scope</label>
                <textarea
                  className="po-textarea"
                  rows={3}
                  placeholder="Briefly describe the scope and objectives…"
                  value={form.projectDesc}
                  onChange={(e) => updateField('projectDesc', e.target.value)}
                />
              </div>

              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Expected Project Duration <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.duration}
                    onChange={(e) => updateField('duration', e.target.value)}
                    required
                  >
                    <option value="">Select duration…</option>
                    <option value="<1m">Less than 1 month</option>
                    <option value="1-3m">1–3 months</option>
                    <option value="3-6m">3–6 months</option>
                    <option value="6-12m">6–12 months</option>
                    <option value="1y+">1 year+</option>
                  </select>
                  {errors.duration && <div className="po-error">{errors.duration}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Industry / Project Type <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.industryProjectType}
                    onChange={(e) => updateField('industryProjectType', e.target.value)}
                    required
                  >
                    <option value="Game Development">Game Development</option>
                    <option value="Animation">Animation</option>
                    <option value="VFX">VFX</option>
                    <option value="Tech">Tech</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.industryProjectType && <div className="po-error">{errors.industryProjectType}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Project Location / Time Zone Alignment <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.projectLocationTimeZone}
                    onChange={(e) => updateField('projectLocationTimeZone', e.target.value)}
                    required
                  >
                    <option value="GMT+8">GMT+8</option>
                    <option value="PST">PST</option>
                    <option value="EST">EST</option>
                    <option value="CET">CET</option>
                    <option value="JST">JST</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.projectLocationTimeZone && <div className="po-error">{errors.projectLocationTimeZone}</div>}
                </div>
              </div>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Reporting Line <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.reportingLine}
                    onChange={(e) => updateField('reportingLine', e.target.value)}
                    required
                  >
                    <option value="Client PM">Client PM</option>
                    <option value="Exodia Supervisor">Exodia Supervisor</option>
                    <option value="Joint Supervision">Joint Supervision</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reportingLine && <div className="po-error">{errors.reportingLine}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Key Milestones & Deliverables <span className="po-help">(optional)</span></label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="List key milestones or outputs…"
                    value={form.milestones}
                    onChange={(e) => updateField('milestones', e.target.value)}
                  />
                </div>
              </div>

              {form.industryProjectType === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify industry/project type</label>
                  <input
                    className="po-input"
                    placeholder="Enter industry/project type..."
                    value={form.industryProjectTypeOther}
                    onChange={(e) => updateField('industryProjectTypeOther', e.target.value)}
                    required
                  />
                  {errors.industryProjectTypeOther && <div className="po-error">{errors.industryProjectTypeOther}</div>}
                </div>
              )}

              {form.projectLocationTimeZone === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify time zone</label>
                  <input
                    className="po-input"
                    placeholder="Enter time zone..."
                    value={form.projectLocationTimeZoneOther}
                    onChange={(e) => updateField('projectLocationTimeZoneOther', e.target.value)}
                    required
                  />
                  {errors.projectLocationTimeZoneOther && <div className="po-error">{errors.projectLocationTimeZoneOther}</div>}
                </div>
              )}

              {form.reportingLine === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify reporting line</label>
                  <input
                    className="po-input"
                    placeholder="Enter reporting line details..."
                    value={form.reportingLineOther}
                    onChange={(e) => updateField('reportingLineOther', e.target.value)}
                    required
                  />
                  {errors.reportingLineOther && <div className="po-error">{errors.reportingLineOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* ====== STEP 2: ENGAGEMENT ====== */}
          {step === 1 && (
            <section className="po-card">
              <h3 className="po-h3">Section 2: Engagement Model</h3>
              <div className="po-grid1">
                {[
                  { id: 'em1', value: 'EOR Standard', label: 'EOR Standard – Full suite under our Employee-of-Record structure' },
                  { id: 'em2', value: 'EOR Premium',  label: 'EOR Premium – Customized onboarding, deployment, and equipment per client specifications' },
                  { id: 'em3', value: 'Non-EOR',      label: 'Non-EOR (Onboarding Only) – For direct hires with deployment support' },
                  { id: 'em4', value: 'White Label',  label: 'White Label – Entire project delivered under your brand identity' },
                ].map(opt => (
                  <label key={opt.id} className="po-radio">
                    <input
                      type="radio"
                      name="engagementModel"
                      value={opt.value}
                      checked={form.engagementModel === opt.value}
                      onChange={(e) => updateField('engagementModel', e.target.value)}
                      required
                    />
                    <span className="po-radiomark" /> <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {errors.engagementModel && <div className="po-error">{errors.engagementModel}</div>}

              <div className="po-field">
                <label className="po-label">Engagement Duration <span className="req">*</span></label>
                <select
                  className="po-input po-select-dark"
                  value={form.engagementDuration}
                  onChange={(e) => updateField('engagementDuration', e.target.value)}
                  required
                >
                  <option value="Short-Term">Short-Term</option>
                  <option value="Mid-Term">Mid-Term</option>
                  <option value="Long-Term">Long-Term</option>
                  <option value="Indefinite">Indefinite</option>
                </select>
                {errors.engagementDuration && <div className="po-error">{errors.engagementDuration}</div>}
              </div>
            </section>
          )}

          {/* ====== STEP 3: TEAM / TALENT ====== */}
          {step === 2 && (
            <section className="po-card">
              <h3 className="po-h3">Section 3: Team / Talent Requirements</h3>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Number of Talents Required</label>
                  <input
                    type="number"
                    min="1"
                    className="po-input"
                    value={form.numberOfTalents}
                    onChange={(e) => updateField('numberOfTalents', e.target.value)}
                    required
                  />
                  {errors.numberOfTalents && <div className="po-error">{errors.numberOfTalents}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Availability Preference</label>
                  <div className="po-grid1">
                    <label className="po-radio">
                      <input
                        type="radio"
                        name="availability"
                        value="full"
                        checked={form.availability === 'full'}
                        onChange={(e) => updateField('availability', e.target.value)}
                        required
                      />
                      <span className="po-radiomark" /> <span>Full-time dedicated to project</span>
                    </label>
                    <label className="po-radio">
                      <input
                        type="radio"
                        name="availability"
                        value="part"
                        checked={form.availability === 'part'}
                        onChange={(e) => updateField('availability', e.target.value)}
                        required
                      />
                      <span className="po-radiomark" /> <span>Part-time / milestone-based</span>
                    </label>
                  </div>
                  {errors.availability && <div className="po-error">{errors.availability}</div>}
                </div>
              </div>

              {/* ROLES NEEDED — now checkboxes powered by role.js */}
              <div className="po-field">
                <label className="po-label">Roles Needed</label>
                {Object.entries(ROLE_GROUPS).map(([group, list]) => (
                  <div key={group} className="po-group">
                    <div className="po-subtitle" style={{ marginBottom: 4 }}>{group}</div>
                    <div className="po-checklist">
                      {list.map((title) => (
                        <label key={title} className="po-check">
                          <input
                            type="checkbox"
                            checked={form.rolesNeeded.includes(title)}
                            onChange={() => toggleRole(title)}
                          />
                          <span>{title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="po-help">Select all that apply. You can also specify an “Other” role below.</div>
              </div>

              <div className="po-field">
                <label className="po-label">Other: specify role</label>
                <input
                  className="po-input"
                  placeholder="e.g., Technical Artist"
                  value={form.otherRole}
                  onChange={(e) => updateField('otherRole', e.target.value)}
                />
                {errors.rolesNeeded && <div className="po-error">{errors.rolesNeeded}</div>}
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Talent Profile Requirements</div>
              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Seniority Level Needed <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.seniorityLevel}
                    onChange={(e) => updateField('seniorityLevel', e.target.value)}
                    required
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid-Level">Mid-Level</option>
                    <option value="Senior">Senior</option>
                  </select>
                  {errors.seniorityLevel && <div className="po-error">{errors.seniorityLevel}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Work Arrangement <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.workArrangement}
                    onChange={(e) => updateField('workArrangement', e.target.value)}
                    required
                  >
                    <option value="Onsite">Onsite</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Remote">Remote</option>
                  </select>
                  {errors.workArrangement && <div className="po-error">{errors.workArrangement}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Shift Requirement <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.shiftRequirement}
                    onChange={(e) => updateField('shiftRequirement', e.target.value)}
                    required
                  >
                    <option value="Day">Day</option>
                    <option value="Night">Night</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                  {errors.shiftRequirement && <div className="po-error">{errors.shiftRequirement}</div>}
                </div>
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Languages Required</div>
              <div className="po-field">
                <label className="po-label">Languages Required</label>
                <div className="po-checklist">
                  {['English', 'Japanese', 'Korean', 'Mandarin'].map((lang) => (
                    <Check
                      key={lang}
                      label={lang}
                      checked={form.languagesRequired.includes(lang)}
                      onChange={(checked) => {
                        if (checked) {
                          setForm(f => ({ ...f, languagesRequired: [...f.languagesRequired, lang] }));
                        } else {
                          setForm(f => ({ ...f, languagesRequired: f.languagesRequired.filter(item => item !== lang) }));
                        }
                      }}
                    />
                  ))}
                  <Check
                    label="Other"
                    checked={form.languagesRequired.includes('Other')}
                    onChange={(checked) => {
                      if (checked) {
                        setForm(f => ({ ...f, languagesRequired: [...f.languagesRequired, 'Other'] }));
                      } else {
                        setForm(f => ({ ...f, languagesRequired: f.languagesRequired.filter(item => item !== 'Other') }));
                      }
                    }}
                  />
                </div>

                {form.languagesRequired.includes('Other') && (
                  <div className="po-field">
                    <label className="po-label">Please specify other languages</label>
                    <input
                      className="po-input"
                      placeholder="Enter other languages..."
                      value={form.languagesOther}
                      onChange={(e) => updateField('languagesOther', e.target.value)}
                      required
                    />
                    {errors.languagesOther && <div className="po-error">{errors.languagesOther}</div>}
                  </div>
                )}
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Tools/Software Required</div>
              <div className="po-field">
                <label className="po-label">Tools/Software Required</label>
                <div className="po-checklist">
                  {['Unity', 'Unreal', 'Blender', 'Photoshop', 'Jira'].map((tool) => (
                    <Check
                      key={tool}
                      label={tool}
                      checked={form.toolsSoftwareRequired.includes(tool)}
                      onChange={(checked) => {
                        if (checked) {
                          setForm(f => ({ ...f, toolsSoftwareRequired: [...f.toolsSoftwareRequired, tool] }));
                        } else {
                          setForm(f => ({ ...f, toolsSoftwareRequired: f.toolsSoftwareRequired.filter(item => item !== tool) }));
                        }
                      }}
                    />
                  ))}
                  <Check
                    label="Other"
                    checked={form.toolsSoftwareRequired.includes('Other')}
                    onChange={(checked) => {
                      if (checked) {
                        setForm(f => ({ ...f, toolsSoftwareRequired: [...f.toolsSoftwareRequired, 'Other'] }));
                      } else {
                        setForm(f => ({ ...f, toolsSoftwareRequired: f.toolsSoftwareRequired.filter(item => item !== 'Other') }));
                      }
                    }}
                  />
                </div>

                {form.toolsSoftwareRequired.includes('Other') && (
                  <div className="po-field">
                    <label className="po-label">Please specify other tools/software</label>
                    <input
                      className="po-input"
                      placeholder="Enter other tools/software..."
                      value={form.toolsSoftwareOther}
                      onChange={(e) => updateField('toolsSoftwareOther', e.target.value)}
                      required
                    />
                    {errors.toolsSoftwareOther && <div className="po-error">{errors.toolsSoftwareOther}</div>}
                  </div>
                )}
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Security/Compliance Needs</div>
              <div className="po-field">
                <label className="po-label">Security/Compliance Needs</label>
                <div className="po-checklist">
                  {['NDA signing', 'IP restrictions', 'Limited access accounts'].map((need) => (
                    <Check
                      key={need}
                      label={need}
                      checked={form.securityComplianceNeeds.includes(need)}
                      onChange={(checked) => {
                        if (checked) {
                          setForm(f => ({ ...f, securityComplianceNeeds: [...f.securityComplianceNeeds, need] }));
                        } else {
                          setForm(f => ({ ...f, securityComplianceNeeds: f.securityComplianceNeeds.filter(item => item !== need) }));
                        }
                      }}
                    />
                  ))}
                  <Check
                    label="Other"
                    checked={form.securityComplianceNeeds.includes('Other')}
                    onChange={(checked) => {
                      if (checked) {
                        setForm(f => ({ ...f, securityComplianceNeeds: [...f.securityComplianceNeeds, 'Other'] }));
                      } else {
                        setForm(f => ({ ...f, securityComplianceNeeds: f.securityComplianceNeeds.filter(item => item !== 'Other') }));
                      }
                    }}
                  />
                </div>

                {form.securityComplianceNeeds.includes('Other') && (
                  <div className="po-field">
                    <label className="po-label">Please specify other security/compliance needs</label>
                    <input
                      className="po-input"
                      placeholder="Enter other security/compliance needs..."
                      value={form.securityComplianceOther}
                      onChange={(e) => updateField('securityComplianceOther', e.target.value)}
                      required
                    />
                    {errors.securityComplianceOther && <div className="po-error">{errors.securityComplianceOther}</div>}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ====== STEP 4: SCREENING ====== */}
          {step === 3 && (
            <section className="po-card">
              <h3 className="po-h3">Section 4: Screening & Assessments</h3>
              
              <div className="po-h4">Assessment Modules</div>
              <div className="po-grid2">
                <label className="po-check">
                  <input
                    type="checkbox"
                    checked={form.modules.behavioral}
                    onChange={(e) => updateNested('modules.behavioral', e.target.checked)}
                  />
                  <span className="po-checkmark" /> <span>Behavioral & Personality Assessments</span>
                </label>
                <label className="po-check">
                  <input
                    type="checkbox"
                    checked={form.modules.skills}
                    onChange={(e) => updateNested('modules.skills', e.target.checked)}
                  />
                  <span className="po-checkmark" /> <span>Skills & Aptitude Testing</span>
                </label>
                <label className="po-check">
                  <input
                    type="checkbox"
                    checked={form.modules.structured}
                    onChange={(e) => updateNested('modules.structured', e.target.checked)}
                  />
                  <span className="po-checkmark" /> <span>Structured Interview Rounds</span>
                </label>
                <label className="po-check">
                  <input
                    type="checkbox"
                    checked={form.modules.custom}
                    onChange={(e) => updateNested('modules.custom', e.target.checked)}
                  />
                  <span className="po-checkmark" /> <span>Custom Module (Contact Sales)</span>
                </label>
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Skills Requirements</div>
              <div className="po-field">
                <label className="po-label">Top 3 Must-Have Skills <span className="req">*</span></label>
                <textarea
                  className="po-textarea"
                  rows={3}
                  placeholder="List the top 3 must-have skills for this project..."
                  value={form.top3MustHaveSkills}
                  onChange={(e) => updateField('top3MustHaveSkills', e.target.value)}
                  required
                />
                {errors.top3MustHaveSkills && <div className="po-error">{errors.top3MustHaveSkills}</div>}
              </div>

              <div className="po-field">
                <label className="po-label">Nice-to-Have Skills</label>
                <textarea
                  className="po-textarea"
                  rows={3}
                  placeholder="List any nice-to-have skills (optional)..."
                  value={form.niceToHaveSkills}
                  onChange={(e) => updateField('niceToHaveSkills', e.target.value)}
                />
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Requirements</div>
              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Portfolio/Work Sample Requirement <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.portfolioRequirement}
                    onChange={(e) => updateField('portfolioRequirement', e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.portfolioRequirement && <div className="po-error">{errors.portfolioRequirement}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Certification Requirement <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.certificationRequirement}
                    onChange={(e) => updateField('certificationRequirement', e.target.value)}
                    required
                  >
                    <option value="Unity Certified">Unity Certified</option>
                    <option value="Scrum Master">Scrum Master</option>
                    <option value="PMP">PMP</option>
                    <option value="None">None</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.certificationRequirement && <div className="po-error">{errors.certificationRequirement}</div>}
                </div>
              </div>

              {form.certificationRequirement === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify certification requirement</label>
                  <input
                    className="po-input"
                    placeholder="Enter certification requirement..."
                    value={form.certificationOther}
                    onChange={(e) => updateField('certificationOther', e.target.value)}
                    required
                  />
                  {errors.certificationOther && <div className="po-error">{errors.certificationOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* ====== STEP 5: DEPLOYMENT ====== */}
          {step === 4 && (
            <section className="po-card">
              <h3 className="po-h3">Section 5: Deployment Preferences</h3>

              <div className="po-h4">Deployment Settings</div>
              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Deployment Mode <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.deploymentMode}
                    onChange={(e) => updateField('deploymentMode', e.target.value)}
                    required
                  >
                    <option value="">Select deployment mode…</option>
                    <option value="standard-30">Standard 30-day deployment</option>
                    <option value="fast-14">Fast-Track 14-day deployment (additional fee per talent)</option>
                  </select>
                  {errors.deploymentMode && <div className="po-error">{errors.deploymentMode}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Work Enablement Setup <span className="req">*</span></label>
                  <div className="po-grid1">
                    <label className="po-radio">
                      <input
                        type="radio"
                        name="setupLevel"
                        value="standard"
                        checked={form.setupLevel === 'standard'}
                        onChange={(e) => updateField('setupLevel', e.target.value)}
                        required
                      />
                      <span className="po-radiomark" /> <span>Standard Workstation & IT Access</span>
                    </label>
                    <label className="po-radio">
                      <input
                        type="radio"
                        name="setupLevel"
                        value="premium"
                        checked={form.setupLevel === 'premium'}
                        onChange={(e) => updateField('setupLevel', e.target.value)}
                        required
                      />
                      <span className="po-radiomark" /> <span>Premium / Custom Workstation Setup</span>
                    </label>
                  </div>
                  {errors.setupLevel && <div className="po-error">{errors.setupLevel}</div>}
                </div>
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Nesting & Go-Live</div>
              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Nesting Duration <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.nestingDuration}
                    onChange={(e) => updateField('nestingDuration', e.target.value)}
                    required
                  >
                    <option value="1 week">1 week</option>
                    <option value="2 weeks">2 weeks</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {errors.nestingDuration && <div className="po-error">{errors.nestingDuration}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Supervision Level <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.supervisionLevel}
                    onChange={(e) => updateField('supervisionLevel', e.target.value)}
                    required
                  >
                    <option value="Light">Light</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Intensive">Intensive</option>
                  </select>
                  {errors.supervisionLevel && <div className="po-error">{errors.supervisionLevel}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Performance Monitoring Frequency <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.performanceMonitoringFrequency}
                    onChange={(e) => updateField('performanceMonitoringFrequency', e.target.value)}
                    required
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-weekly">Bi-weekly</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {errors.performanceMonitoringFrequency && <div className="po-error">{errors.performanceMonitoringFrequency}</div>}
                </div>
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Go-Live & Knowledge Transfer</div>
              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Go-Live Readiness Confirmation <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.goLiveReadinessConfirmation}
                    onChange={(e) => updateField('goLiveReadinessConfirmation', e.target.value)}
                    required
                  >
                    <option value="Client-led">Client-led</option>
                    <option value="Exodia-led">Exodia-led</option>
                    <option value="Joint">Joint</option>
                  </select>
                  {errors.goLiveReadinessConfirmation && <div className="po-error">{errors.goLiveReadinessConfirmation}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Knowledge Transfer Method <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.knowledgeTransferMethod}
                    onChange={(e) => updateField('knowledgeTransferMethod', e.target.value)}
                    required
                  >
                    <option value="Documentation">Documentation</option>
                    <option value="Shadowing">Shadowing</option>
                    <option value="Recorded sessions">Recorded sessions</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.knowledgeTransferMethod && <div className="po-error">{errors.knowledgeTransferMethod}</div>}
                </div>
              </div>

              {form.knowledgeTransferMethod === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify knowledge transfer method</label>
                  <input
                    className="po-input"
                    placeholder="Enter knowledge transfer method..."
                    value={form.knowledgeTransferOther}
                    onChange={(e) => updateField('knowledgeTransferOther', e.target.value)}
                    required
                  />
                  {errors.knowledgeTransferOther && <div className="po-error">{errors.knowledgeTransferOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* ====== STEP 6: OFFBOARDING ====== */}
          {step === 5 && (
            <section className="po-card">
              <h3 className="po-h3">Section 6: Offboarding Preference</h3>
              
              <div className="po-help" style={{ marginBottom: 8 }}>
                Offboarding will be managed by Exodia to ensure smooth and compliant project closure.
              </div>
              <label className="po-check">
                <input
                  type="checkbox"
                  checked={form.offboardingConfirm}
                  onChange={(e) => updateField('offboardingConfirm', e.target.checked)}
                  required
                />
                <span className="po-checkmark" />
                <span>"I confirm Exodia will manage offboarding of all project talents upon completion, unless otherwise extended."</span>
              </label>
              {errors.offboardingConfirm && <div className="po-error">{errors.offboardingConfirm}</div>}

              <div className="po-section-divider" />
              <div className="po-h4">Exit Interview</div>
              <div className="po-field">
                <label className="po-label">Exit Interview Required? <span className="req">*</span></label>
                <select
                  className="po-input po-select-dark"
                  value={form.exitInterviewRequired}
                  onChange={(e) => updateField('exitInterviewRequired', e.target.value)}
                  required
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="Client-led">Client-led</option>
                  <option value="Exodia-led">Exodia-led</option>
                  <option value="Joint">Joint</option>
                </select>
                {errors.exitInterviewRequired && <div className="po-error">{errors.exitInterviewRequired}</div>}
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Final Payroll & Benefits</div>
              <div className="po-field">
                <label className="po-label">Final Payroll & Benefits Clearance Required? <span className="req">*</span></label>
                <select
                  className="po-input po-select-dark"
                  value={form.finalPayrollBenefitsClearance}
                  onChange={(e) => updateField('finalPayrollBenefitsClearance', e.target.value)}
                  required
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
                {errors.finalPayrollBenefitsClearance && <div className="po-error">{errors.finalPayrollBenefitsClearance}</div>}
              </div>

              {form.finalPayrollBenefitsClearance === 'Yes' && (
                <div className="po-field">
                  <label className="po-label">Payroll & Benefits Notes <span className="req">*</span></label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="Provide details about payroll and benefits clearance requirements..."
                    value={form.payrollBenefitsNotes}
                    onChange={(e) => updateField('payrollBenefitsNotes', e.target.value)}
                    required
                  />
                  {errors.payrollBenefitsNotes && <div className="po-error">{errors.payrollBenefitsNotes}</div>}
                </div>
              )}
            </section>
          )}

          {/* ====== STEP 7: NOTES & SPECIAL INSTRUCTIONS ====== */}
          {step === 6 && (
            <section className="po-card">
              <h3 className="po-h3">Section 7: Notes & Special Instructions</h3>
              
              <div className="po-h4">Budget Information</div>
              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Budget Range (per role or per hire) <span className="req">*</span></label>
                  <input
                    className="po-input"
                    placeholder="e.g., $3000-5000"
                    value={form.budgetRange}
                    onChange={(e) => updateField('budgetRange', e.target.value)}
                    required
                  />
                  {errors.budgetRange && <div className="po-error">{errors.budgetRange}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Maximum Budget Cap (optional)</label>
                  <input
                    className="po-input"
                    type="number"
                    placeholder="e.g., 100000"
                    value={form.maxBudgetCap}
                    onChange={(e) => updateField('maxBudgetCap', e.target.value)}
                  />
                </div>

                <div className="po-field">
                  <label className="po-label">Payment Preference <span className="req">*</span></label>
                  <select
                    className="po-input po-select-dark"
                    value={form.paymentPreference}
                    onChange={(e) => updateField('paymentPreference', e.target.value)}
                    required
                  >
                    <option value="Per milestone">Per milestone</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Per talent">Per talent</option>
                  </select>
                  {errors.paymentPreference && <div className="po-error">{errors.paymentPreference}</div>}
                </div>
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Special Instructions & Goals</div>
              <div className="po-field">
                <label className="po-label">Special Instructions / Additional Requirements</label>
                <textarea
                  className="po-textarea"
                  rows={4}
                  placeholder="Add any special requests or operational notes..."
                  value={form.specialInstructions}
                  onChange={(e) => updateField('specialInstructions', e.target.value)}
                />
              </div>

              <div className="po-field">
                <label className="po-label">Top 3 Must-Have Project Goals <span className="req">*</span></label>
                <textarea
                  className="po-textarea"
                  rows={3}
                  placeholder="e.g., Playable demo ready within 2 months"
                  value={form.top3MustHaveProjectGoals}
                  onChange={(e) => updateField('top3MustHaveProjectGoals', e.target.value)}
                  required
                />
                {errors.top3MustHaveProjectGoals && <div className="po-error">{errors.top3MustHaveProjectGoals}</div>}
              </div>

              <div className="po-field">
                <label className="po-label">Success Metrics / KPIs <span className="req">*</span></label>
                <textarea
                  className="po-textarea"
                  rows={3}
                  placeholder="We consider the project successful if..."
                  value={form.successMetricsKPIs}
                  onChange={(e) => updateField('successMetricsKPIs', e.target.value)}
                  required
                />
                {errors.successMetricsKPIs && <div className="po-error">{errors.successMetricsKPIs}</div>}
              </div>

              <div className="po-section-divider" />
              <div className="po-h4">Point of Contact for Project</div>
              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Full Name <span className="req">*</span></label>
                  <input
                    className="po-input"
                    placeholder="Enter full name"
                    value={form.pointOfContact.fullName}
                    onChange={(e) => updateNested('pointOfContact.fullName', e.target.value)}
                    required
                  />
                  {errors.pointOfContactFullName && <div className="po-error">{errors.pointOfContactFullName}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Email Address <span className="req">*</span></label>
                  <input
                    className="po-input"
                    type="email"
                    placeholder="Enter email address"
                    value={form.pointOfContact.email}
                    onChange={(e) => updateNested('pointOfContact.email', e.target.value)}
                    required
                  />
                  {errors.pointOfContactEmail && <div className="po-error">{errors.pointOfContactEmail}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Phone/WhatsApp <span className="req">*</span></label>
                  <input
                    className="po-input"
                    placeholder="Enter phone/WhatsApp"
                    value={form.pointOfContact.phoneWhatsapp}
                    onChange={(e) => updateNested('pointOfContact.phoneWhatsapp', e.target.value)}
                    required
                  />
                  {errors.pointOfContactPhone && <div className="po-error">{errors.pointOfContactPhone}</div>}
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 8: CONFIRMATION ====== */}
          {step === 7 && (
            <section className="po-card">
              <h3 className="po-h3">Section 8: Confirmation</h3>
              <label className="po-check">
                <input
                  type="checkbox"
                  checked={form.confirm}
                  onChange={(e) => updateField('confirm', e.target.checked)}
                  required
                />
                <span className="po-checkmark" />
                <span>“I confirm this request for Project services. Exodia will manage talent sourcing, onboarding, deployment, and offboarding as per my selected engagement model.”</span>
              </label>
              {errors.confirm && <div className="po-error">{errors.confirm}</div>}
            </section>
          )}

          {/* ---------- WIZARD FOOTER ---------- */}
          <div className="po-footer po-footer--wizard">
            <div className="po-footer-left">
              <button type="button" className="po-btn po-btn-ghost" onClick={saveDraft}>Save Draft</button>
              <button type="button" className="po-btn po-btn-ghost" onClick={clearDraft}>
                Clear Draft
              </button>
            </div>
            <div className="po-footer-right">
              <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
              {step > 0 && (
                <button type="button" className="po-btn" onClick={goBack}>Back</button>
              )}
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  className="po-btn po-btn-primary"
                  onClick={(e) => { e.preventDefault(); goNext(); }}
                  formNoValidate
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="po-btn po-btn-primary">Submit</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
